/**
 * src/auth/apiFetch.js
 *
 * Thin fetch wrapper that attaches an Entra access token as
 * `Authorization: Bearer <token>` when one is available, and otherwise
 * proceeds without it. This is the reusable primitive the Phase 2/3 JWT
 * principal-auth migration routes api modules onto.
 *
 * Token contract (post-Gate-3.6): token acquisition is best-effort at this
 * layer — if getApiToken() returns null (not logged in / interaction
 * required) or throws, we send the request WITHOUT the Authorization header,
 * and apiFetch() itself does not throw. That is NOT graceful degradation.
 * Gate 3.6 closed the tokenless window on the backend: a header-less request
 * whose caller_email resolves to an active user is now rejected 401
 * ("tokenless identity assertion rejected"), not served. A header-less
 * request is a failed request — it just fails at the server, not here.
 *
 * caller_email is NOT handled here. Callers still append it to the URL, but
 * it no longer substitutes for a token on the tokenless path. Removing the
 * appends is Gate 5, not now.
 *
 * apiFetch() is a transparent wrapper: method, body, query params, and any
 * caller-supplied headers pass through unchanged. It only injects the one
 * Authorization header.
 */

import { getApiToken } from './msalInstance';

export async function apiFetch(url, options = {}) {
  let token = null;
  try {
    token = await getApiToken();
  } catch (e) {
    // Unexpected acquisition failure — degrade to the caller_email path.
    // Do NOT propagate: a token problem must not break the request.
    console.warn('apiFetch: token acquisition failed, proceeding without Bearer', e);
    token = null;
  }

  const headers = token
    ? { ...(options.headers || {}), Authorization: `Bearer ${token}` }
    : options.headers;

  return fetch(url, { ...options, headers });
}
